/**
 * @fileoverview Hook de Data Layer para gestión y filtrado de conversaciones en el Inbox.
 * Proporciona lógica optimizada para filtros, ordenamiento y paginación, integrando Redux y modelos API.
 * Cumple estrictamente la separación de responsabilidades de la Data Layer.
 *
 * @module useInboxFilters
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 3.1.0
 */

import {
  useState,
  useCallback,
  useMemo,
  useEffect
} from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import ConversationModelMC from '../../../models/inbox/ConversationModelMC';
import {
  fetchConversationsByFilter,
  setActiveInboxFilter,
  setActiveInboxSort
} from '../../../../actions/inbox/conversationActions';

const useInboxFilters = () => {
  const dispatch = useDispatch();

  // Selectores de estado global (solo datos, sin lógica de UI)
  const { loading, error } = useSelector(
    (reduxState) => reduxState.conversationsReducer
  );

  // Estado centralizado para filtros y ordenamiento
  const defaultFilter = useMemo(() => ({
    conversation: 'all',
    mia: null,
    channel: null,
    platformRetailerId: null,
    msPlatform: null,
    searchTerm: '',
    quickFilters: { activity: [], status: ['pending'] },
    advancedFilters: {}
  }), []);

  // Función para determinar si los filtros actuales son los valores por defecto (sin personalización)
  const defaultSortConfig = useMemo(() => ({
    field: 'received',
    direction: 'desc'
  }), []);

  // Función para comparar filtros actuales con los valores por defecto, ignorando claves específicas
  const isDefaultFilter = useCallback((filter, sortConfig) => {
    const omitKeys = ['conversation', 'mia', 'channel', 'platformRetailerId', 'msPlatform'];
    const clean = (obj) => {
      const cleaned = { ...obj, searchTerm: obj.searchTerm?.trim?.() || '' };
      if (cleaned.quickFilters) {
        const normalizedQuickFilters = {
          ...cleaned.quickFilters
        };
        delete normalizedQuickFilters.isSelectingConversations;
        cleaned.quickFilters = normalizedQuickFilters;
      }
      omitKeys.forEach((key) => {
        delete cleaned[key];
      });
      return cleaned;
    };
    const filterString = JSON.stringify(clean(filter));
    const defaultString = JSON.stringify(clean(defaultFilter));
    return (
      filterString === defaultString
      && JSON.stringify(sortConfig) === JSON.stringify(defaultSortConfig)
    );
  }, [defaultFilter, defaultSortConfig]);

  // Estado local para filtros, ordenamiento y si hay filtros personalizados
  const [state, setState] = useState(() => ({
    filter: { ...defaultFilter },
    sortConfig: { ...defaultSortConfig },
    hasCustomFilters: false
  }));

  // Función para limpiar parámetros eliminando valores vacíos o nulos, y formateando arrays
  const cleanParams = useCallback((obj) => Object.entries(obj).reduce((acc, [key, value]) => {
    if (
      value === undefined
      || value === null
      || (typeof value === 'string' && value.trim() === '')
      || (Array.isArray(value) && value.length === 0)
    ) {
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {}), []);

  // Construye los parámetros de consulta para la API basándose en el estado actual de filtros y ordenamiento, memoizando el resultado para evitar cálculos innecesarios
  const buildQueryParams = useMemo(() => {
    const rawParams = {
      activity: state.filter.quickFilters?.activity?.join(','),
      agents: state.filter.advancedFilters?.agent_id?.join(','),
      campaigns: state.filter.advancedFilters?.campaign_id?.join(','),
      contact_groups: state.filter.advancedFilters?.contact_group_id?.join(','),
      conversations: state.filter.conversation,
      funnel: state.filter.advancedFilters?.funnel_id,
      funnel_steps: state.filter.advancedFilters?.funnel_steps_id_in?.join(','),
      mia: state.filter.mia,
      order: `${state.sortConfig.field}_${state.sortConfig.direction}`,
      platform: state.filter.msPlatform || state.filter.channel,
      platform_retailer_id: state.filter.platformRetailerId,
      searchString: state.filter.searchTerm?.trim() || '',
      status: state.filter.quickFilters?.status?.join(','),
      tags_in: Array.isArray(state.filter.advancedFilters?.tags_tag_id_in)
        ? state.filter.advancedFilters.tags_tag_id_in
          .map((arr) => (Array.isArray(arr) ? arr.join(',') : (arr?.id ?? arr)))
          .join(';')
        : undefined,
      tags_out: Array.isArray(state.filter.advancedFilters?.tags_tag_id_not_in)
        ? state.filter.advancedFilters.tags_tag_id_not_in
          .map((arr) => (Array.isArray(arr) ? arr.join(',') : (arr?.id ?? arr)))
          .join(';')
        : undefined,
      lead_temperature: state.filter.advancedFilters?.lead_temperature || undefined
    };
    return { params: cleanParams(rawParams) };
  }, [state, cleanParams]);

  /**
   * Indica si hay una búsqueda activa por término de búsqueda.
   */
  const hasActiveSearch = useMemo(
    () => state.filter.searchTerm.trim().length > 0,
    [state.filter.searchTerm]
  );

  /**
   * Actualiza el filtro según la sección y el valor proporcionado.
   */
  const updateFilter = useCallback((section, filterKey) => {
    if (loading) return;

    setState((prevState) => {
      const newFilter = { ...prevState.filter };
      switch (section) {
        case 'conversation':
          newFilter.conversation = filterKey;
          break;
        case 'mia':
          newFilter.mia = prevState.filter.mia === filterKey ? null : filterKey;
          break;
        case 'channel':
          newFilter.channel = filterKey;
          newFilter.platformRetailerId = null;
          newFilter.msPlatform = null;
          break;
        case 'platformRetailer':
          newFilter.channel = filterKey?.channel ?? prevState.filter.channel;
          newFilter.platformRetailerId = filterKey?.platformRetailerId ?? null;
          newFilter.msPlatform = filterKey?.msPlatform ?? null;
          break;
        case 'searchTerm':
          newFilter.searchTerm = filterKey || '';
          break;
        case 'quickFilters':
          newFilter.quickFilters = filterKey || { activity: [], status: ['pending'] };
          break;
        case 'advancedFilters':
          newFilter.advancedFilters = filterKey || {};
          break;
        default:
          break;
      }
      // Evita cambiar la referencia si el contenido es igual
      if (_.isEqual(prevState.filter, newFilter)) {
        return prevState;
      }

      // Actualiza el filtro activo en Redux para que otros componentes puedan reaccionar
      dispatch(setActiveInboxFilter(newFilter));

      const hasCustomFilters = !isDefaultFilter(
        newFilter,
        prevState.sortConfig
      );
      return { ...prevState, filter: newFilter, hasCustomFilters };
    });
  }, [loading, isDefaultFilter, dispatch]);

  /**
   * Actualiza la configuración de ordenamiento.
   */
  const updateSortConfig = useCallback((newSortConfig) => {
    if (loading) return;

    setState((prevState) => {
      // Evita cambiar la referencia si el contenido es igual
      if (_.isEqual(prevState.sortConfig, newSortConfig)) {
        return prevState;
      }

      dispatch(setActiveInboxSort(newSortConfig));

      const hasCustomFilters = !isDefaultFilter(
        prevState.filter,
        newSortConfig
      );
      return { ...prevState, sortConfig: newSortConfig, hasCustomFilters };
    });
  }, [loading, isDefaultFilter, dispatch]);

  /**
   * Restablece filtros, orden y paginación a los valores iniciales por defecto.
   *
   * @function resetFilters
   * @memberof useInboxFilters
   * @returns {void}
   */
  const resetFilters = useCallback(() => {
    if (loading) return;

    const newFilter = {
      ...defaultFilter,
      conversation: state.filter.conversation,
      mia: state.filter.mia,
      channel: state.filter.channel,
      platformRetailerId: state.filter.platformRetailerId,
      msPlatform: state.filter.msPlatform
    };
    const newSortConfig = { ...defaultSortConfig };

    dispatch(setActiveInboxFilter(newFilter));
    dispatch(setActiveInboxSort(newSortConfig));

    setState({
      filter: newFilter,
      sortConfig: newSortConfig,
      hasCustomFilters: false
    });
  }, [
    loading,
    defaultFilter,
    defaultSortConfig,
    state.filter.conversation,
    state.filter.mia,
    state.filter.channel,
    state.filter.platformRetailerId,
    state.filter.msPlatform,
    dispatch
  ]);

  // Función para cargar conversaciones según los filtros y ordenamiento actuales, con soporte de paginación
  const loadConversations = useCallback(async (page = 1) => {
    dispatch(fetchConversationsByFilter({ page, ...buildQueryParams.params }));
  }, [buildQueryParams.params, dispatch]);

  // Función para cargar el conteo de conversaciones no leídas, útil para mostrar badges o notificaciones
  const loadUnreadCount = useCallback(async () => {
    try {
      const response = await ConversationModelMC.getUnreadCount();
      return response.count || 0;
    } catch {
      return 0;
    }
  }, []);

  // Efecto para establecer los filtros y ordenamiento activos en Redux al montar el hook, asegurando que el estado global esté sincronizado con el estado local inicial
  useEffect(() => {
    dispatch(setActiveInboxFilter(state.filter));
    dispatch(setActiveInboxSort(state.sortConfig));
  }, []);

  return useMemo(() => ({
    // Datos
    filter: state.filter,
    sortConfig: state.sortConfig,
    searchTerm: state.filter.searchTerm,
    hasActiveSearch,
    hasCustomFilters: state.hasCustomFilters,
    loading,
    error,

    // Funciones estables
    updateFilter,
    updateSortConfig,
    resetFilters,
    loadConversations,
    loadUnreadCount,
    // Query params (ya memoizado)
    buildQueryParams
  }), [
    // Estado (necesario para actualizar cuando cambie)
    state.filter,
    state.sortConfig,
    hasActiveSearch,
    state.hasCustomFilters,
    loading,
    error,

    // Funciones (ya son estables por useCallback)
    updateFilter,
    updateSortConfig,
    resetFilters,
    loadConversations,
    loadUnreadCount,
    buildQueryParams
  ]);
};

export default useInboxFilters;
